import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, X } from 'lucide-react';
import Button from '../ui/Button';
import type { InductionGap, InductionMode } from '../../types/induction';

interface Props {
  gaps: InductionGap[];
  /** The caller holds an assessor seat and may sign people off here. */
  canOverride: boolean;
  /** Answer the refusal: re-send the request with this mode. */
  onChoose: (mode: InductionMode, note: string) => void;
  onClose: () => void;
  busy?: boolean;
  error?: string | null;
}

interface PersonGaps {
  name: string;
  machines: string[];
  certificates: { machine: string; names: string[] }[];
}

function groupByPerson(gaps: InductionGap[]): PersonGaps[] {
  const map = new Map<string, PersonGaps>();
  for (const g of gaps) {
    const key = g.person_key;
    if (!map.has(key)) map.set(key, { name: g.name || 'Someone', machines: [], certificates: [] });
    const p = map.get(key)!;
    if (g.reason === 'certificate') {
      p.certificates.push({ machine: g.equipment_name, names: g.missing_certificates || [] });
    } else if (!p.machines.includes(g.equipment_name)) {
      p.machines.push(g.equipment_name + (g.reason === 'expired' ? ' (expired)' : ''));
    }
  }
  return [...map.values()];
}

/**
 * A job was refused because someone on it is not inducted on one of its
 * machines. Three honest answers:
 *
 *   - sign them off now (assessors only) — recorded as a supervisor override
 *   - save the job without them and ask a supervisor, who adds them when they
 *     sign them off
 *   - go back and change the people or the machines
 *
 * A missing certification (licence) has no override: it has to go on the
 * Staff Certifications register first.
 */
export default function InductionGapDialog({ gaps, canOverride, onChoose, onClose, busy, error }: Props) {
  const [note, setNote] = useState('');
  const people = groupByPerson(gaps);
  const onlyCertificates = gaps.every(g => g.reason === 'certificate');
  const anyCertificates = gaps.some(g => g.reason === 'certificate');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Not inducted">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[calc(100dvh-2rem)] flex flex-col">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-warning-600 shrink-0" />
            {onlyCertificates ? 'Certification needed' : 'Not inducted'}
          </h3>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto text-sm text-neutral-700">
          <ul className="space-y-3">
            {people.map((p, i) => (
              <li key={i} className="rounded-lg border border-neutral-200 p-3">
                <div className="font-medium text-neutral-900 break-words">{p.name}</div>
                {p.machines.length > 0 && (
                  <div className="mt-1 break-words">Not inducted on {p.machines.join(', ')}</div>
                )}
                {p.certificates.map((c, j) => (
                  <div key={j} className="mt-1 text-error-700 break-words">
                    Needs a current {c.names.join(' and ')} on file for {c.machine}
                  </div>
                ))}
              </li>
            ))}
          </ul>

          {anyCertificates && (
            <p className="rounded-lg bg-error-50 p-3 text-error-700">
              A certification cannot be signed off here. Record it on the{' '}
              <Link to="/certifications/admin" className="underline">Staff Certifications</Link> register, then try again.
            </p>
          )}

          {!onlyCertificates && !anyCertificates && (
            <>
              <p>
                {canOverride
                  ? 'You can sign them off now if you have seen them operate these machines safely, or save the job without them and have another supervisor do it.'
                  : 'A supervisor has to sign them off first. Save the job without them and a supervisor will be asked — they are added to the job as soon as they are signed off.'}
              </p>
              {canOverride && (
                <div>
                  <label htmlFor="induction-note" className="block text-sm font-medium text-neutral-700 mb-1">Note (optional)</label>
                  <textarea
                    id="induction-note"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    rows={2}
                    placeholder="e.g. Watched them hitch up and slash the headland"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}
            </>
          )}

          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 break-words">{error}</div>}
        </div>

        <div className="px-5 py-3 border-t border-neutral-200 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {anyCertificates ? 'Close' : 'Go back'}
          </Button>
          {!anyCertificates && (
            <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => onChoose('request', note)}>
              Save without them, ask a supervisor
            </Button>
          )}
          {!anyCertificates && canOverride && (
            <Button type="button" variant="primary" size="sm" disabled={busy} onClick={() => onChoose('induct', note)}>
              {busy ? 'Saving…' : 'Sign them off and save'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
